import clsx from 'clsx';
import { FunctionComponent, PointerEvent, PropsWithChildren, useCallback, useContext, useRef, useState } from 'react';

import { GlobalsContext } from '../CommonAppContext';
import style from './DragMove.module.css';

export interface Movement {
  x: number;
  y: number;
}

export interface DragMoveProps extends PropsWithChildren {
  draggingEnabled?: boolean;
  blockEvents?: boolean;
  onPointerUp?: () => void;
  onPointerDown?: (point: Movement) => void;
  onPointerCancel?: () => void;
  onDragMove: (movement: Movement) => void;
  fullSize?: boolean;
}

export const DragMove: FunctionComponent<DragMoveProps> = ({
  draggingEnabled,
  blockEvents,
  onDragMove,
  onPointerUp,
  onPointerDown,
  onPointerCancel,
  fullSize,
  children,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [start, setStart] = useState<Movement>({ x: 0, y: 0 });
  const { isTouchDevice } = useContext(GlobalsContext);
  const alwaysDisablePointerCapture = !isTouchDevice;
  const captureRef = useRef<HTMLDivElement>(null);

  const startPointerCapture = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!captureRef.current || alwaysDisablePointerCapture) {
        return;
      }

      captureRef.current.setPointerCapture(e.pointerId);
    },
    [alwaysDisablePointerCapture]
  );

  const endPointerCapture = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!captureRef.current || alwaysDisablePointerCapture) {
        return;
      }

      captureRef.current.releasePointerCapture(e.pointerId);
    },
    [alwaysDisablePointerCapture]
  );

  const startDraggingIfNeeded = useCallback(
    (e: PointerEvent<HTMLDivElement>, disablePointerCapture?: boolean) => {
      if (!draggingEnabled) {
        return;
      }
      if (isDragging) {
        return;
      }

      if (!disablePointerCapture) {
        startPointerCapture(e);
      }

      setStart({ x: e.clientX, y: e.clientY });
      setIsDragging(true);

      if (onPointerDown) {
        onPointerDown({ x: e.clientX, y: e.clientY });
      }
    },
    [isDragging, draggingEnabled, onPointerDown, startPointerCapture]
  );

  const isSecondaryClick = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const { pointerType, button } = e;
    return pointerType === 'mouse' && button === 2;
  }, []);

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>, disablePointerCapture?: boolean) => {
      // block ctrl click button events
      if (isSecondaryClick(e)) {
        return;
      }

      if (blockEvents) {
        e.preventDefault();
        e.stopPropagation();
      }

      startDraggingIfNeeded(e, disablePointerCapture);
    },
    [blockEvents, isSecondaryClick, startDraggingIfNeeded]
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!draggingEnabled) {
        return;
      }

      if (isTouchDevice) {
        startDraggingIfNeeded(e);

        if (blockEvents) {
          e.preventDefault();
          e.stopPropagation();
        }
      }

      if (isDragging) {
        onDragMove({
          x: e.clientX - start.x,
          y: e.clientY - start.y,
        });
      }
    },
    [draggingEnabled, isTouchDevice, blockEvents, isDragging, startDraggingIfNeeded, onDragMove, start.x, start.y]
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent<HTMLDivElement>, disablePointerCapture?: boolean) => {
      if (blockEvents) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!disablePointerCapture) {
        endPointerCapture(e);
      }

      setIsDragging(false);
      if (onPointerUp) {
        onPointerUp();
      }
    },
    [blockEvents, endPointerCapture, onPointerUp]
  );

  const handlePointerCancel = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!isDragging) {
        return;
      }

      if (blockEvents) {
        e.preventDefault();
        e.stopPropagation();
      }

      endPointerCapture(e);

      setIsDragging(false);
      if (onPointerCancel) {
        onPointerCancel();
      }
    },
    [isDragging, blockEvents, endPointerCapture, onPointerCancel]
  );

  const handleOnClick = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      handlePointerDown(e, true);
      handlePointerUp(e, true);
    },
    [handlePointerDown, handlePointerUp]
  );

  return (
    // skipcq JS-0746, JS-0765
    <div
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerMove={handlePointerMove}
      onPointerCancel={handlePointerCancel}
      onClick={handleOnClick}
      className={clsx(style.root, { [style.draggingEnabled]: draggingEnabled }, { [style.fullSize]: !!fullSize })}
      ref={captureRef}
    >
      {children}
    </div>
  );
};

DragMove.defaultProps = {
  draggingEnabled: true,
};
